import React, { useState } from 'react';
import { Toaster } from 'react-hot-toast';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import CallToAction from './components/CallToAction';
import Features from './components/Features';
import EstimationForm from './components/EstimationForm';
import MarketTrends from './components/MarketTrends';
import QuickEstimate from './components/QuickEstimate';
import Services from './components/Services';
import Results from './components/Results';
import FAQ from './components/FAQ';
import Modal from './components/Modal';
import CookieConsent from './components/CookieConsent';

function App() {
  const [showEstimationForm, setShowEstimationForm] = useState(false);
  const [initialAddress, setInitialAddress] = useState('');

  const handleStartEstimation = (address: string) => {
    setInitialAddress(address);
    setShowEstimationForm(true);
  };

  const handleCloseEstimationForm = () => {
    setShowEstimationForm(false);
    setInitialAddress('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      <Toaster />
      <CookieConsent />
      
      {/* En-tête du site */}
      <header>
        <Navbar />
      </header>

      {/* Section principale - Hero */}
      <section id="accueil" aria-label="Estimation immobilière à Nice">
        <Hero />
      </section>

      {/* Section Call to Action */}
      <CallToAction />

      {/* Section Avantages */}
      <section id="avantages" aria-label="Nos avantages" className="pb-8">
        <Features />
      </section>

      {/* Section Estimation Rapide */}
      <section 
        id="estimation-rapide" 
        aria-label="Estimation rapide de votre bien"
        className="relative py-12"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <QuickEstimate onStartEstimation={handleStartEstimation} />
        </div>
      </section>

      {/* Section Services */}
      <section 
        id="services" 
        aria-label="Nos services d'estimation"
      >
        <Services />
      </section>

      {/* Section Résultats */}
      <section 
        id="resultats" 
        aria-label="Résultats d'évaluation"
      >
        <Results />
      </section>

      {/* Section Tendances du Marché */}
      <section 
        id="tendances-marche" 
        aria-label="Tendances du marché immobilier à Nice"
        className="pt-8"
      >
        <MarketTrends />
      </section>

      {/* Section FAQ */}
      <FAQ />

      {/* Modal du formulaire d'estimation */}
      <Modal 
        isOpen={showEstimationForm} 
        onClose={handleCloseEstimationForm}
      >
        <EstimationForm 
          initialAddress={initialAddress} 
          visible={showEstimationForm}
          onClose={handleCloseEstimationForm}
        />
      </Modal>

      {/* Pied de page */}
      <footer className="mt-16 relative mb-10">
        <div className="w-full mx-auto relative">
          <div className="bg-white/80 backdrop-blur-xl shadow-[0_8px_32px_rgba(42,67,101,0.15)] 
            transform hover:scale-[1.02] transition-all duration-300 
            before:content-[''] before:absolute before:inset-0
            before:bg-gradient-to-b before:from-white/30 before:to-[#2A4365]/10 before:pointer-events-none
            [transform-style:preserve-3d] hover:[transform:perspective(1000px)_rotateX(2deg)]
            after:content-[''] after:absolute after:inset-0
            after:bg-gradient-to-r after:from-[#2A4365]/10 after:via-[#4A5568]/10 after:to-[#C4A052]/10 
            after:pointer-events-none relative overflow-hidden">
            
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 relative z-10">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="transform transition-transform duration-300 hover:-translate-y-1 hover:text-blue-600 flex items-center pl-0">
                  <img 
                    src="https://studio.estimhabitat.com/storage/v1/object/public/app-img//EstimHabitat_logo_middle.png"
                    alt="ESTIM HABITAT Logo"
                    className="h-12 sm:h-16 w-auto object-contain"
                  />
                </div>
                <div className="transform transition-transform duration-300 hover:-translate-y-1 hover:text-blue-600">
                  <h3 className="text-lg font-semibold mb-4 text-gray-900">Contact</h3>
                  <p className="text-gray-600">
                    Email: contact@estimhabitat.com<br />
                    Tél: +33 7 80 90 00 00
                  </p>
                  <div className="mt-4 flex justify-center">
                    <img 
                      src="https://studio.estimhabitat.com/storage/v1/object/public/app-img//estimhabitat_qrcode.png"
                      alt="QR Code ESTIM HABITAT"
                      className="h-24 w-24 object-contain"
                    />
                  </div>
                </div>
                <div className="transform transition-transform duration-300 hover:-translate-y-1">
                  <h3 className="text-lg font-semibold mb-4 text-gray-900">Navigation</h3>
                  <ul className="space-y-2 text-gray-600">
                    <li>
                      <a href="#accueil" className="hover:text-blue-600 transition-colors">
                        Accueil
                      </a>
                    </li>
                    <li>
                      <a href="#avantages" className="hover:text-blue-600 transition-colors">
                        Nos avantages
                      </a>
                    </li>
                    <li>
                      <a href="#estimation-rapide" className="hover:text-blue-600 transition-colors">
                        Estimation rapide
                      </a>
                    </li>
                    <li>
                      <a href="#tendances-marche" className="hover:text-blue-600 transition-colors">
                        Tendances du marché
                      </a>
                    </li>
                    <li>
                      <a href="#faq" className="hover:text-blue-600 transition-colors">
                        FAQ
                      </a>
                    </li>
                    <li>
                      <a href="/privacy-policy" className="hover:text-blue-600 transition-colors">
                        Politique de confidentialité
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200/50 text-center text-gray-600">
                <p>© {new Date().getFullYear()} ESTIM HABITAT. Tous droits réservés.</p>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;